'use client';

import { c as _c } from "react/compiler-runtime";
import type { GraphQLCommitment, GraphQLDeparture, GraphQLDepartureSub } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { findDepartureCommitment, isDepartureCommitment } from '@sola/shared-utils/supporters/departures.ts';
import { useDepartureSummary, useSponsorshipsAny } from '@sola/supporter-sdk/hooks/commitments.ts';
import { sortSponsorshipCommitmentsByParticipantName } from '@sola/supporter-sdk/utils/commitments.ts';
export type SponsorshipsDepartureItem = {
  departure: GraphQLDeparture;
  departedChild: GraphQLDeparture['departedChild'];
  departedCommitment?: GraphQLCommitment;
  offeredSub?: GraphQLDepartureSub;
};
export type SponsorshipsListModel = {
  departureItems: SponsorshipsDepartureItem[];
  sponsorships: GraphQLCommitment[];
  isLoading: boolean;
  hasError: boolean;
};
type GetSponsorshipsListModelArgs = {
  departures?: GraphQLDeparture[];
  hasError?: boolean;
  isLoading?: boolean;
  sponsorships?: GraphQLCommitment[];
};
export const getDepartureChildName = (child: GraphQLDeparture['departedChild']): string => (child.displayName ?? '').trim();
const getOfferedSub = (departure: GraphQLDeparture): GraphQLDepartureSub | undefined => {
  const offeredSub = departure.sub;
  return offeredSub?.shouldDisplay === true && offeredSub.completed === false ? offeredSub : undefined;
};
export const getSponsorshipsListModel = ({
  departures = [],
  hasError = false,
  isLoading = false,
  sponsorships = []
}: GetSponsorshipsListModelArgs): SponsorshipsListModel => {
  const departureItems = departures.toSorted((a, b) => getDepartureChildName(a.departedChild).localeCompare(getDepartureChildName(b.departedChild))).map(departure => ({
    departure,
    departedChild: departure.departedChild,
    departedCommitment: findDepartureCommitment(sponsorships, departure.departedChild),
    offeredSub: getOfferedSub(departure)
  }));
  const regularSponsorships = sortSponsorshipCommitmentsByParticipantName(sponsorships.filter(commitment => !departureItems.some(({
    departedChild,
    offeredSub
  }) => {
    const isDepartedChild = isDepartureCommitment(commitment, departedChild);
    const isOfferedSub = !!offeredSub && (isDepartureCommitment(commitment, offeredSub.child) || commitment.internalId === offeredSub.pendingCommitmentId);
    return isDepartedChild || isOfferedSub;
  })));
  return {
    departureItems,
    sponsorships: regularSponsorships,
    isLoading,
    hasError
  };
};
export const useSponsorshipsList = () => {
  const $ = _c(5);
  const sponsorshipsQuery = useSponsorshipsAny();
  const departureSummaryQuery = useDepartureSummary();
  const isLoading = sponsorshipsQuery.isLoading || sponsorshipsQuery.isFetching || sponsorshipsQuery.isPending || departureSummaryQuery.isLoading || departureSummaryQuery.isFetching || departureSummaryQuery.isPending;
  const t0 = departureSummaryQuery.departureSummary?.departures;
  const t1 = sponsorshipsQuery.isError || departureSummaryQuery.isError;
  let t2;
  if ($[0] !== isLoading || $[1] !== sponsorshipsQuery.sponsorships || $[2] !== t0 || $[3] !== t1) {
    t2 = getSponsorshipsListModel({
      departures: t0,
      hasError: t1,
      isLoading,
      sponsorships: sponsorshipsQuery.sponsorships
    });
    $[0] = isLoading;
    $[1] = sponsorshipsQuery.sponsorships;
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};